import React, { useState } from 'react';
import Input from 'components/Inputs/Input';
import CampaignObjective from 'components/Campaigns/CreateEdit/Objective';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { DetailsType } from 'containers/Campaigns/CreateEdit/store/reducer';
import { CampaignObjectiveKey } from 'utils/types';
import Collapse from 'react-bootstrap/Collapse';
import './styles.scss';
import parseISO from 'date-fns/parse';
import format from 'date-fns/format';

interface Props {
  details: DetailsType;
  error?: string;
  onNameBlur(name: string, storeGroupName: string, itemGroupName: string): void;
  onValueChange(value: string, attribute: string): void;
}

const CreateEditCampaignDetailsForm: React.FC<Props> = ({
  details,
  error = '',
  onNameBlur,
  onValueChange,
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const handleDateValue = (date: Date, attribute: string) => {
    onValueChange(format(date, 'YYYY-MM-DD'), attribute);
  };

  return (
    <div className="create-edit-campaigns-details-form">
      <div className="input-container">
        <Input
          label="Campaign Name"
          placeholder="Campaign Name"
          value={details.name}
          onBlur={(value) => onNameBlur(value, details.storeGroupName, details.itemGroupName)}
          onChange={(value) => onValueChange(value, 'name')}
        />
      </div>
      <div className="store-item-name-container">
        <div className="input-container">
          <Input
            label="Store Group Name"
            placeholder="Store Group Name"
            value={details.storeGroupName}
            onChange={(value) => onValueChange(value, 'storeGroupName')}
          />
        </div>
        <div className="input-container">
          <Input
            label="Item Group Name"
            placeholder="Item Group Name"
            value={details.itemGroupName}
            onChange={(value) => onValueChange(value, 'itemGroupName')}
          />
        </div>
      </div>
      <div className="input-container">
        <div className="campaign-objective-title">Campaign Objective</div>
        <div>
          <div className="campaign-objective-container">
            {[
              CampaignObjectiveKey.AUDIT,
              CampaignObjectiveKey.PRICING_COMPLIANCE,
              CampaignObjectiveKey.PLACEMENT,
            ].map((key) => (
              <CampaignObjective
                key={key}
                selected={details.objective === key}
                type={key}
                onClick={(type) => onValueChange(type, 'objective')}
              />
            ))}
          </div>
          <div className="campaign-objective-container">
            {[
              CampaignObjectiveKey.NEW_PRODUCT_INTRO,
              CampaignObjectiveKey.IN_STORE_DEMO,
              CampaignObjectiveKey.IN_STORE_PROMOTION,
            ].map((key) => (
              <CampaignObjective
                key={key}
                selected={details.objective === key}
                type={key}
                onClick={(type) => onValueChange(type, 'objective')}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="date-picker-container">
        <div>
          <label>Start Date</label>
          <DatePicker
            label="Start Date"
            className="date-picker-input"
            maxDate={parseISO(details.endDate)}
            placeholderText="Start Date"
            selected={details.startDate !== '' ? parseISO(details.startDate) : null}
            onChange={(value) => handleDateValue(value, 'startDate')}
          />
        </div>
        <div>
          <label>End Date</label>
          <DatePicker
            label="End Date (optional)"
            className="date-picker-input"
            minDate={details.startDate !== '' ? parseISO(details.startDate) : null}
            placeholderText="End Date (optional)"
            selected={details.endDate !== '' ? parseISO(details.endDate) : null}
            onChange={(value) => handleDateValue(value, 'endDate')}
          />
        </div>
      </div>
      <div className="input-container">
        <Input
          label="Notes (optional)"
          placeholder="Campaign Notes"
          value={details.notes}
          onChange={(value) => onValueChange(value, 'notes')}
        />
      </div>
      {error && error !== '' && (
        <div className="error-message">
          <div className="more-details-container">
            <div>An error occurred while saving the campaign</div>
            <div className="more-details" onClick={() => setShowDetails(!showDetails)}>
              {!showDetails ? 'More details' : 'Less details'}
            </div>
          </div>
          <Collapse in={showDetails} timeout={200}>
            <div className="message">{error}</div>
          </Collapse>
        </div>
      )}
    </div>
  );
};

export default CreateEditCampaignDetailsForm;
